/**
 * Creating a bundle — the loot table.
 *
 * The slot editor is the point of this screen. A bundle is an ordered list of
 * positions, each drawing from its own weighted set of rewards, which is how
 * "five things, and the fifth is a rare" gets said.
 *
 * Two things are shown live because getting either wrong is invisible until
 * an open fails in front of an audience:
 *
 *   - the ODDS each slot actually produces, normalised from the weights, so
 *     nobody has to make a row add up to 100; and
 *   - the PACK SIZE, summed across slots, because twenty slots of three is
 *     sixty collectibles while every individual bound passes.
 *
 * Entries are resolved against the loaded rewards AS THEY ARE TYPED. The
 * client check is a HINT and the server remains authoritative — it runs the
 * same resolution against the database and refuses the save regardless.
 * Duplicated pool logic is what diverged on the predecessors (L15); here the
 * worst case if they disagree is a warning that is wrong, never a bad document.
 */
import { useState, type ReactNode } from 'react';
import { MAX_PACK_SIZE, MAX_SLOTS, type BundleInput, type Reward } from '@tomo/contracts';
import { Shell } from '../components/Shell.jsx';
import { Badge, Button, Failed, Spinner } from '../components/primitives.jsx';
import { Choice, Field, Num, Supply, Text, supplyValue, type SupplyChoice } from '../components/form.jsx';
import { bundles, rewards as rewardsApi } from '../lib/tomo.js';
import { ApiError } from '../lib/api.js';
import { useAsync } from '../useAsync.js';
import { navigate, hrefFor } from '../lib/router.js';

type Weighting = 'explicit' | 'by_remaining';

interface DraftEntry {
  rewardId: string;
  weight: number;
}

interface DraftSlot {
  label: string;
  count: number;
  weighting: Weighting;
  entries: DraftEntry[];
}

const emptyEntry = (): DraftEntry => ({ rewardId: '', weight: 1 });
const emptySlot = (): DraftSlot => ({ label: '', count: 1, weighting: 'explicit', entries: [emptyEntry()] });

export function NewBundle({ channelId }: { channelId: string }): ReactNode {
  const [catalog] = useAsync((signal) => rewardsApi.list(channelId, signal), [channelId]);

  if (catalog.state === 'loading') {
    return <Shell channelId={channelId}><Spinner label="Loading rewards…" /></Shell>;
  }
  if (catalog.state === 'failed') {
    return (
      <Shell channelId={channelId}>
        <Failed message="Could not load this channel’s rewards." detail={catalog.error.message} />
      </Shell>
    );
  }
  return <Form channelId={channelId} rewards={catalog.value} />;
}

function Form({ channelId, rewards }: { channelId: string; rewards: Reward[] }): ReactNode {
  const [name, setName] = useState('');
  const [slots, setSlots] = useState<DraftSlot[]>([emptySlot()]);
  const [supply, setSupply] = useState<SupplyChoice>('unlimited');
  const [limit, setLimit] = useState(100);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const active = rewards.filter((r) => r.active);
  const packSize = slots.reduce((n, s) => n + s.count, 0);

  /** What is wrong with one entry, in words, or null. */
  const entryProblem = (slot: DraftSlot, entry: DraftEntry, i: number): string | null => {
    if (entry.rewardId.length === 0) return 'Pick a reward for this row to draw.';
    const found = rewards.find((r) => r.id === entry.rewardId);
    if (!found) return 'No reward on this channel has that id.';
    // Inactive is the same as absent to selection, so a slot pointing only at
    // one can never fill — and a slot that cannot fill cancels the whole pack.
    if (!found.active) return 'That reward is inactive, so it can never be drawn.';
    if (slot.entries.findIndex((e) => e.rewardId === entry.rewardId) !== i) {
      return 'This slot already names that reward. Combine the rows into one weight.';
    }
    return null;
  };

  /** What is wrong with a whole slot, beyond its individual rows. */
  const slotProblem = (slot: DraftSlot): string | null =>
    slot.weighting === 'explicit' && !slot.entries.some((e) => e.weight > 0)
      ? 'Every weight here is zero, so this slot could never draw anything.'
      : null;

  const problems = slots.map((slot) => ({
    slot: slotProblem(slot),
    entries: slot.entries.map((e, i) => entryProblem(slot, e, i)),
  }));

  const ready =
    name.length > 0 &&
    packSize <= MAX_PACK_SIZE &&
    problems.every((p) => p.slot === null && p.entries.every((e) => e === null));

  const fields = error?.byField() ?? {};

  const updateSlot = (i: number, patch: Partial<DraftSlot>): void => {
    setSlots((list) => list.map((s, n) => (n === i ? { ...s, ...patch } : s)));
  };
  const updateEntry = (si: number, ei: number, patch: Partial<DraftEntry>): void => {
    setSlots((list) => list.map((s, n) => (n === si
      ? { ...s, entries: s.entries.map((e, m) => (m === ei ? { ...e, ...patch } : e)) }
      : s)));
  };

  const submit = (): void => {
    setBusy(true);
    setError(null);
    const input: BundleInput = {
      name,
      slots: slots.map((s) => ({
        // Sent as ABSENT rather than empty: the wire schema has `label`
        // optional with a minimum length, so '' would fail its own validation.
        ...(s.label.trim().length > 0 ? { label: s.label.trim() } : {}),
        count: s.count,
        weighting: s.weighting,
        entries: s.entries.map((e) => ({ reward_id: e.rewardId, weight: e.weight })),
      })),
      supply: supplyValue(supply, limit),
      active: true,
    };
    bundles.create(channelId, input).then(
      ({ id }) => { navigate(hrefFor([channelId, 'bundles', id])); },
      (err: unknown) => {
        setBusy(false);
        setError(err instanceof ApiError
          ? err
          : new ApiError('malformed_response', 0, 'Something unexpected went wrong.'));
      }
    );
  };

  return (
    <Shell channelId={channelId}>
      <h1 className="text-xl font-semibold tracking-tight">New bundle</h1>
      <p className="mt-1 text-sm text-ink-400">
        A loot table. Each slot is one position in the pack, drawing from its own
        set of rewards.
      </p>

      {error ? <div className="mt-6"><Failed message={error.message} /></div> : null}

      <form
        className="mt-8 max-w-3xl space-y-6"
        onSubmit={(e) => { e.preventDefault(); submit(); }}
      >
        <Field label="Name" error={fields.name}>
          <Text value={name} onChange={setName} placeholder="Basement Box" maxLength={120} />
        </Field>

        <fieldset>
          <legend className="text-sm text-ink-200">Slots</legend>
          <p className="mt-0.5 text-xs text-ink-400">
            Drawn in order. Every slot is filled or the pack does not open — a pack
            is never handed over short.
          </p>

          <ul className="mt-3 space-y-4">
            {slots.map((slot, si) => (
              <li key={si} className="rounded-lg border border-ink-800 p-4">
                <div className="flex flex-wrap items-end gap-3">
                  <span className="w-8 shrink-0 pb-2 text-sm tabular-nums text-ink-400">
                    {si + 1}
                  </span>

                  <span className="min-w-44 flex-1">
                    <span className="mb-1 block text-xs text-ink-400">Label (optional)</span>
                    <Text
                      value={slot.label}
                      onChange={(label) => { updateSlot(si, { label }); }}
                      placeholder="Guaranteed rare"
                      maxLength={60}
                    />
                  </span>

                  <span className="w-20">
                    <span className="mb-1 block text-xs text-ink-400">Draws</span>
                    <Num value={slot.count} onChange={(count) => { updateSlot(si, { count }); }} min={1} max={20} />
                  </span>

                  {slots.length > 1 ? (
                    <button
                      type="button"
                      className="pb-2 text-xs text-ink-400 underline"
                      onClick={() => { setSlots((l) => l.filter((_, n) => n !== si)); }}
                    >
                      Remove slot
                    </button>
                  ) : null}
                </div>

                <div className="mt-3">
                  <Field
                    label="Odds come from"
                    hint={slot.weighting === 'by_remaining'
                      ? 'Each reward’s remaining stock. The print runs you configured ARE the odds — the weights below are ignored.'
                      : 'The weights below, normalised. 3 and 1 means the same as 75 and 25.'}
                  >
                    <Choice
                      value={slot.weighting}
                      onChange={(weighting) => { updateSlot(si, { weighting }); }}
                      options={[
                        { value: 'explicit', label: 'Weights I set' },
                        { value: 'by_remaining', label: 'Remaining stock' },
                      ]}
                    />
                  </Field>
                </div>

                <ul className="mt-3 space-y-2">
                  {slot.entries.map((entry, ei) => (
                    <li key={ei}>
                      <div className="flex flex-wrap items-end gap-3">
                        <span className="min-w-48 flex-1">
                          <Choice
                            value={entry.rewardId}
                            onChange={(rewardId) => { updateEntry(si, ei, { rewardId }); }}
                            options={[
                              { value: '', label: 'Pick a reward…' },
                              ...rewards.map((r) => ({
                                value: r.id,
                                label: r.active ? r.name : `${r.name} (inactive)`,
                              })),
                            ]}
                          />
                        </span>

                        {slot.weighting === 'explicit' ? (
                          <>
                            <span className="w-24">
                              <span className="mb-1 block text-xs text-ink-400">Weight</span>
                              <Num
                                value={entry.weight}
                                onChange={(weight) => { updateEntry(si, ei, { weight }); }}
                                min={0}
                                max={1000}
                              />
                            </span>
                            <span className="w-14 pb-2 text-right text-sm tabular-nums text-ink-400">
                              {share(slot, ei)}
                            </span>
                          </>
                        ) : (
                          <span className="w-24 pb-2 text-xs text-ink-400">by stock</span>
                        )}

                        {slot.entries.length > 1 ? (
                          <button
                            type="button"
                            className="pb-2 text-xs text-ink-400 underline"
                            onClick={() => {
                              setSlots((l) => l.map((s, n) => (n === si
                                ? { ...s, entries: s.entries.filter((_, m) => m !== ei) }
                                : s)));
                            }}
                          >
                            Remove
                          </button>
                        ) : null}
                      </div>

                      {problems[si]?.entries[ei] ? (
                        <p role="alert" className="mt-1 text-xs text-warn-400">
                          {problems[si]?.entries[ei]}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>

                {problems[si]?.slot ? (
                  <p role="alert" className="mt-2 text-xs text-warn-400">{problems[si]?.slot}</p>
                ) : null}

                <button
                  type="button"
                  className="mt-3 text-sm text-brand-300 underline"
                  onClick={() => {
                    setSlots((l) => l.map((s, n) => (n === si
                      ? { ...s, entries: [...s.entries, emptyEntry()] }
                      : s)));
                  }}
                >
                  Add a reward to this slot
                </button>
              </li>
            ))}
          </ul>

          {slots.length < MAX_SLOTS ? (
            <button
              type="button"
              className="mt-3 text-sm text-brand-300 underline"
              onClick={() => { setSlots((l) => [...l, emptySlot()]); }}
            >
              Add a slot
            </button>
          ) : null}
        </fieldset>

        <Supply
          choice={supply}
          limit={limit}
          onChange={(c, n) => { setSupply(c); setLimit(n); }}
          error={fields['supply.limit']}
        />

        {packSize > MAX_PACK_SIZE ? (
          <p role="alert" className="rounded-md border border-warn-400/40 bg-warn-400/5 p-3 text-xs text-warn-400">
            {packSize} collectibles per pack is over the limit of {MAX_PACK_SIZE}. Every
            slot mints inside one transaction, and that is the ceiling it fits in.
          </p>
        ) : null}

        {active.length === 0 ? (
          <p className="rounded-md border border-warn-400/40 bg-warn-400/5 p-3 text-xs text-warn-400">
            This channel has no active rewards yet. Create one first, or this bundle
            has nothing to give out.
          </p>
        ) : null}

        <div className="flex items-center gap-3">
          <Button type="submit" disabled={busy || !ready}>
            {busy ? 'Creating…' : 'Create bundle'}
          </Button>
          <Button variant="ghost" onClick={() => { navigate(hrefFor([channelId, 'bundles'])); }}>
            Cancel
          </Button>
          <Badge>
            {slots.length} {slots.length === 1 ? 'slot' : 'slots'} · {packSize} per pack
          </Badge>
        </div>
      </form>
    </Shell>
  );
}

/**
 * One entry's share of its slot, as a percentage.
 *
 * Shown rather than demanded. Their slot editor made every row total exactly
 * 100 and it is a rule people fight; weights normalise, so 3 and 1 says the
 * same thing as 75 and 25 and neither needs correcting.
 */
function share(slot: DraftSlot, i: number): string {
  const total = slot.entries.reduce((n, e) => n + Math.max(0, e.weight), 0);
  if (total <= 0) return '—';
  return `${String(Math.round((Math.max(0, slot.entries[i]?.weight ?? 0) / total) * 100))}%`;
}
